import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  inject,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core'
import { ModalStockOperationCardComponent } from '../../components/modal-components/modal-stock-operation-card/modal-stock-operation-card.component'
import { File, ProductMove, ProductMovePack } from 'depoto-core/src/entities'
import { Events } from 'depoto-core/src/utils'
import { FileUtilService } from '../../utils/file-util.service'
import { SchemaUtil, unknownMimeType, getFulfilled, getRejected } from '../../utils'
import { CoreService, NotificationService } from '../../services'
import { ModalSerialNumbersComponent } from '../../modal-serial-numbers/modal-serial-numbers.component'
import { TranslateService } from '@ngx-translate/core'
import { PrintCodeComponent } from '../../components/print-code/print-code.component'
import { BaseListing } from '../_base-listing/base-listing'
import { DepotService } from '../../services/entity-services/depot.service'
import { SupplierService } from '../../services/entity-services/supplier.services'
import { ProducerService } from '../../services/entity-services/producer.service'
import { injectInfiniteQuery, injectQuery } from '@tanstack/angular-query-experimental'
import { VatService } from '../../services/entity-services/vat.services'
import { ProductService } from '../../services/entity-services/product.service'
import { Subscription } from 'rxjs'

type OperationType = 'in' | 'out' | 'transfer'

declare let disableBarcodeScanner: boolean

const productSchema: { [key: string]: any } = SchemaUtil.productRestricted

@Component({
  selector: 'stock-operation-list',
  templateUrl: 'stock-operation-list.component.html',
  styleUrls: ['stock-operation-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class StockOperationListComponent extends BaseListing implements OnInit, OnDestroy {
  private productService = inject(ProductService)
  private depotService = inject(DepotService)
  private supplierService = inject(SupplierService)
  private producerService = inject(ProducerService)
  private vatService = inject(VatService)

  // @ViewChild modalStatus was not in scope. Has been deleted
  @ViewChild('modalStockOperation') modalStockOperation: ModalStockOperationCardComponent
  @ViewChild('printCodeComponent') printCodeComponent: PrintCodeComponent
  @ViewChild('serialNumbersGlobal') modalSerialNumbers: ModalSerialNumbersComponent
  // Queue for sequentially opening serial-number modals for multiple moves
  private serialQueue: ProductMove[] = []
  private serialQueueRunning = false
  private serialQueueSubs: Subscription[] = []
  private currentSerialMove: ProductMove | null = null
  operations: ProductMove[] = []
  movesOriginPositions: { productMoveUuid: number | string; originPosition: string }[] = []
  operationTypes: string[] = ['in', 'out', 'transfer']
  note = ''
  externalReference = ''
  currentPage: number
  endPage: number
  optionsVisible = false
  selectedOperationType: OperationType | string = 'in'
  selectedDepotFrom = signal<number | null>(null)
  selectedDepotTo: number | null
  searchFulltext = ''
  dateOperation = ''
  initDateDUZP: string
  sortProp = signal('id')
  sortDir = signal('asc')
  isPrintingEANs = false
  uploadedFiles: File[] = []
  areFilesUploading = false
  keyEventSubscriptions: any = {}
  showAvailabilityWarning = false
  // Track last depot to avoid unnecessary refetch on initial effect run
  private _lastSelectedDepotFrom: number | null = null
  // View settings depend on depots being loaded (for validating a stored depot id still exists),
  // so they're restored once from the depots query effect below instead of synchronously in ngOnInit.
  private _hasLoadedViewSettings = false

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        // TODO CORE USER
        return []
      } else {
        return await this.depotService
          .getDepots(
            {
              sort: 'name',
              direction: 'asc',
            },
            { id: null, name: null, enabled: null }
          )
          .then(result => {
            return result.items
          })
      }
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots.filter(d => d.enabled) // TODO CORE USER
      } else {
        return (data as any[]).filter(d => d.enabled)
      }
    },
  }))

  readonly querySuppliers = injectQuery(() => ({
    queryKey: ['suppliers'],
    queryFn: async () => {
      return await this.supplierService
        .getSuppliers(
          {
            sort: 'name',
            direction: 'asc',
          },
          { id: null, name: null }
        )
        .then(result => {
          return result.items
        })
    },
  }))

  readonly queryProducers = injectQuery(() => ({
    queryKey: ['producers'],
    queryFn: async () => {
      return await this.producerService.getProducers({}, { id: null, name: null }).then(result => {
        return result.items
      })
    },
  }))

  readonly queryVats = injectQuery(() => ({
    queryKey: ['vats'],
    queryFn: async () => {
      return await this.vatService.getVats({}, { id: null, name: null, percent: null, default: null }).then(result => {
        return result.items
      })
    },
  }))

  // pageNo = 1,
  // sortProp: string = this.sortProp(),
  // sortDir: string = this.sortDir(),
  // concat = false
  readonly queryProducts = injectInfiniteQuery(() => ({
    queryKey: ['products', this.sortProp(), this.sortDir(), this.selectedDepotFrom()], //this.filtersKey()
    queryFn: async ({ pageParam }) => {
      if (this.selectedOperationType === 'transfer') {
        productSchema.productDepots = {
          id: null,
          depot: { id: null, name: null, unavailablesUrl: null },
          supplier: { id: null, name: null },
          purchasePrice: null,
          quantityStock: null,
          quantityReservation: null,
          quantityAvailable: null,
          inventoryQuantityStock: null,
          batch: null,
          expirationDate: null,
          position: null,
          position1: null,
          position2: null,
          position3: null,
          created: null,
          updated: null,
        }
      } else if (productSchema.productDepots) {
        delete productSchema.productDepots
      }
      // this.loading = true

      const requestId = ++this.currentRequestId

      const result = await this.productService.getProducts(
        {
          page: pageParam, //pageNo
          sort: this.sortProp(),
          direction: this.sortDir(),
          filters: this.getProductFilters(),
        },
        productSchema
      )

      // Initial product loading can finish before the saved batch has been restored.
      if (this._hasLoadedViewSettings) {
        this.saveViewSettings()
      }

      return result
    },
    initialPageParam: 1,
    getNextPageParam: result =>
      Number(result?.paginator?.current) < Number(result?.paginator?.endPage)
        ? Number(result?.paginator?.current) + 1
        : undefined,
  }))

  areCoreDataLoading = computed(() => {
    return this.queryDepotsOptions.isFetching() || this.queryDepotsOptions.isPending()
  })

  products = computed<any[]>(() => {
    // Do not call this.queryProducts as a function; it's an Angular Query result object
    const data = this.queryProducts?.data?.()

    // Plain array
    if (Array.isArray(data)) return data

    // InfiniteQuery shape { pages: [...] }
    const pages = (data as any)?.pages
    if (Array.isArray(pages)) {
      return pages.flatMap((p: any) => (Array.isArray(p?.items) ? p.items : []))
    }

    // Fallback
    return []
  })

  totalItems = computed(() => this.queryProducts.data()?.pages?.[0]?.paginator?.totalCount ?? 0)

  loadingFirstPage = computed(() => !this.queryProducts.isFetchingNextPage() && this.queryProducts.isFetching())
  loadingNextPage = computed(() => this.queryProducts.isFetchingNextPage())
  // For InfiniteData, root-level `items` doesn't exist; rely solely on hasNextPage()
  nextButtonShow = computed(() => this.queryProducts.hasNextPage() && this.products()?.length > 0)

  constructor(
    private notificationService: NotificationService,
    private translateService: TranslateService,
    public core: CoreService,
    private fileUtil: FileUtilService
  ) {
    super(core)
    this.storageKey.set(this.getStorageKeyForEntity('stockOperationList'))
    effect(() => {
      const depotsData = this.queryDepotsOptions.data()
      this.querySuppliers.data()
      this.queryProducts.data()
      this.queryProducers.data()
      this.queryVats.data()
      // Restore persisted filters/selection once depots are available, so a stored
      // depot id can be validated against the current depot list before being applied.
      if (depotsData !== undefined && !this._hasLoadedViewSettings) {
        this._hasLoadedViewSettings = true
        this.loadViewSettings()
        this.refreshOptionsData()
      }
    })

    // Explicitly refetch when selectedDepotFrom changes to keep list up to date.
    // Guard with last value to avoid duplicate refetches on no-op updates.
    effect(() => {
      const depotId = this.selectedDepotFrom()
      if (depotId !== this._lastSelectedDepotFrom) {
        this._lastSelectedDepotFrom = depotId
        this.queryProducts.refetch()
      }
    })
    Events.listen('filters:clear', () => {
      if (window['DEBUG'] && window['DEBUG'].events) {
        console.log('events:filters-clear ')
      }
      this.clearOptions()
      this.clearList(true)
    })
  }
  async ngOnInit() {
    this.keyEventSubscriptions.scannedBarcode = this.core.services.keyEvent.onScannedBarcode.subscribe().then(code => {
      if (disableBarcodeScanner) {
        return
      }
      if (code?.startsWith('pmp-')) {
        return this.addMovesFromPMP(code)
      }
      this.searchFulltext = code
      const refetchResult = this.queryProducts.refetch({ cancelRefetch: true, throwOnError: true }).catch(() => null)
      refetchResult.then(result => {
        if (!result) return
        const products = result.data?.pages.flatMap(page => (Array.isArray(page?.items) ? page.items : [])) ?? []
        if (!products?.length) {
          if (this.selectedOperationType === 'in') {
            this.modalStockOperation.showProductOnChildModal(null, true, this.searchFulltext)
            this.searchFulltext = ''
          } else {
            this.showAvailabilityWarning = true
          }
        } else if (this.selectedOperationType === 'out') {
          if (products?.length === 1) {
            this.modalStockOperation.showProductOnChildModal(products[0])
            this.searchFulltext = ''
            // this.products = []
          } else if (products?.length > 1) {
            this.searchFulltext = ''
            // do nothing just list found items
          }
        } else if (this.selectedOperationType === 'transfer') {
          const productDepotsOnStock = []
          for (const product of products) {
            this.getProductDepotsWithQuantity(product).forEach(pD => productDepotsOnStock.push(pD))
          }
          if (!productDepotsOnStock?.length) {
            this.showAvailabilityWarning = true
          } else if (productDepotsOnStock?.length === 1) {
            const productToSend = products.find(p => p.productDepots.some(pD => pD.id === productDepotsOnStock[0]?.id))
            this.modalStockOperation.showProductDepotOnChildModal(productDepotsOnStock[0], productToSend)
            // this.products = []
            this.searchFulltext = ''
          } else if (productDepotsOnStock?.length > 1) {
            // do nothing just list found items
          }
        }
      })
    })
    // this.loading = false
  }

  ngOnDestroy() {
    if (this.keyEventSubscriptions.scannedBarcode) {
      this.keyEventSubscriptions.scannedBarcode.unsubscribe()
      this.keyEventSubscriptions.scannedBarcode = null
    }
  }

  unknownType(s?: string) {
    return unknownMimeType(s)
  }

  loadViewSettings(): void {
    const settings = this.core.services.storage.getSync(this.storageKey()) || {}
    this.selectedOperationType = settings.selectedOperationType || 'in'
    const depotIds = (this.queryDepotsOptions.data() ?? []).map(d => d.id)
    const doesSelectedDepotToExist = settings.selectedDepotTo && depotIds.includes(Number(settings.selectedDepotTo))
    const doesSelectedDepotFromExist =
      settings.selectedDepotFrom && depotIds.includes(Number(settings.selectedDepotFrom))

    if (this.selectedOperationType === 'in') {
      this.selectedDepotTo = doesSelectedDepotToExist ? settings.selectedDepotTo : null
      this.selectedDepotFrom.set(null)
    } else if (this.selectedOperationType === 'out') {
      this.selectedDepotFrom.set(doesSelectedDepotFromExist ? settings.selectedDepotFrom : null)
      this.selectedDepotTo = null
    } else {
      this.selectedDepotTo = doesSelectedDepotToExist ? settings.selectedDepotTo : null
      this.selectedDepotFrom.set(doesSelectedDepotFromExist ? settings.selectedDepotFrom : null)
    }
    this.optionsVisible = settings.optionsVisible || false
    this.operations = settings.operations?.length ? settings.operations.map(op => new ProductMove(op)) : []
    this.movesOriginPositions = settings.movesOriginPositions?.length ? settings.movesOriginPositions : []
    this.searchFulltext = settings.searchFulltext || ''
    this.dateOperation = settings.dateOperation || ''
    this.sortProp.set(settings.sortProp || 'id')
    this.sortDir.set(settings.sortDir || 'asc')
    this.note = settings.note || ''
    this.externalReference = settings.externalReference || ''
    this.uploadedFiles = settings.uploadedFiles?.length ? settings.uploadedFiles.map(f => new File(f)) : []
  }

  saveViewSettings(): void {
    const settings = {
      optionsVisible: this.optionsVisible,
      selectedOperationType: this.selectedOperationType,
      selectedDepotFrom: this.selectedDepotFrom(),
      selectedDepotTo: this.selectedDepotTo,
      operations: this.operations,
      movesOriginPositions: this.movesOriginPositions,
      dateOperation: this.dateOperation,
      searchFulltext: this.searchFulltext,
      sortProp: this.sortProp(),
      sortDir: this.sortDir(),
      note: this.note,
      externalReference: this.externalReference,
      uploadedFiles: this.uploadedFiles,
    }
    this.core.services.storage.set(this.storageKey(), settings)
  }

  refreshOptionsData(): void {
    // todo co tady ted
    // todo tady ty depots maji byt vyfiltrovane takhle -> resit v te quer\
    // this.depots = this.core.lists.depots.filter(d => d.enabled)
    if (this.queryDepotsOptions.data() && this.queryDepotsOptions.data().length < 2) {
      this.operationTypes = ['in', 'out', 'transfer']
      if (this.queryDepotsOptions.data().length === 1) {
        if (!this.selectedDepotTo) this.selectedDepotTo = this.queryDepotsOptions.data()[0].id
        if (!this.selectedDepotFrom()) this.selectedDepotFrom.set(this.queryDepotsOptions.data()[0].id)
      }
    }
  }

  async clearList(withoutConfirmation = false): Promise<boolean> {
    if (!withoutConfirmation && !confirm(this.translateService.instant('stock-operation-list.delete.confirm'))) {
      // todo stock-in to stock-op ...
      return false
    }
    if (this.uploadedFiles.length) {
      for (const [index, file] of this.uploadedFiles.entries()) {
        try {
          await this.core.services.file.delete({ id: file.id }) // TODO CORE MUTATION
          this.uploadedFiles[index] = undefined
        } catch (e) {
          console.warn(e)
        }
      }
      this.uploadedFiles = this.uploadedFiles.filter(f => f)
      if (this.uploadedFiles.length) {
        this.notificationService.error(
          this.translateService.instant('stock-operation-list.delete.error', {
            filename: this.uploadedFiles.map(f => f.originalFilename).join(', '),
          })
        )
      }
    } // Temporarily 'undeletable' files remain tied to the view
    this.operations = []
    this.note = ''
    this.externalReference = ''
    this.showAvailabilityWarning = false
    this.searchFulltext = ''
    this.saveViewSettings()
    return true
  }

  selectedDepotFromChanged(event) {
    this.selectedDepotFrom.set(event)
    this.saveViewSettings()
  }

  selectedDepotToChanged(event) {
    this.selectedDepotTo = event
    this.saveViewSettings()
  }

  clearOptions() {
    this.selectedDepotFrom.set(null)
    this.selectedDepotTo = null
    this.dateOperation = ''
    this.searchFulltext = ''
    // this.products = []
    this.note = ''
    this.externalReference = ''
    this.showAvailabilityWarning = false
    this.core.services.storage.clear(this.storageKey())
  }

  async onPointerDown(event: PointerEvent) {
    const select = event.currentTarget as HTMLSelectElement
    if (select.id == 'depotSelectTo' && this.selectedOperationType === 'transfer') {
      return // ? no need to prevent select ?
    }
    if (this.operations?.filter(o => o.product).length && !select.dataset.done) {
      event.preventDefault()
      if (await this.clearList()) {
        if (select.id === 'depotSelectFrom') {
          this.selectedDepotFrom.set(null)
        }
        if (select.id === 'depotSelectTo') {
          this.selectedDepotTo = null
        }
        this.core.services.storage.clear(this.storageKey())
        select.dataset.done = 'true'
        select.focus()
        setTimeout(() => {
          try {
            select.showPicker()
          } catch (e) {
            // the browser may want trusted event
            console.warn(e)
          }
        }, 0) //after view updates
      }
    } else {
      select.removeAttribute('data-done')
    }
  }

  filterKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      // this.filter()
      this.queryProducts.refetch()
    }
  }

  private currentRequestId = 0

  getProductFilters() {
    const filters: any = {
      listType: 'children_and_parents_without_children',
      fulltext: this.searchFulltext.trim(),
      enabled: true,
      isBundle: false,
    }
    if (this.selectedOperationType === 'transfer') {
      filters.availability = 'stock'
    }
    if (this.selectedOperationType !== 'in' && this.selectedDepotFrom()) {
      filters.depots = [Number(this.selectedDepotFrom())]
      filters.availability = 'stock'
    }
    if (this.selectedDepotFrom()) {
      filters.depots = [Number(this.selectedDepotFrom())]
    }
    if (!this.selectedDepotFrom() && this.core.services.user.user?.depots?.length) {
      filters.depots = this.core.services.user.user.depots.map(depot => Number(depot.id))
    }
    return filters
  }

  importBatchFromFile(operations: ProductMove[]): void {
    operations.forEach(op => this.addOperationToList(op))
    this.operations = [...this.operations]
    //setTimeout(() => {
    //  this.appRef.tick()
    //}, 50)
  }

  createBatchFromFile(res: any[]): void {
    if (res && res.length > 0) {
      const errorsInResponse = res.filter(r => Array.isArray(r) && typeof r[0] === 'string')
      if (errorsInResponse.length > 0) {
        this.notificationService.error(
          this.translateService.instant('stock-operation-list.create.error', { error: errorsInResponse.join(',') }),
          null,
          true
        )
        console.warn('list::createBatchFromFile errors: ', res)
      } else {
        // this.notificationService.success(this.translateService.instant('stock-operation-list.create.success'))
        this.queryProducts.refetch()
      }
    }
  }

  openModalSerialNumbers(productMove: ProductMove): void {
    // Buffer incoming requests and process them one-by-one
    this.serialQueue.push(productMove)
    if (!this.serialQueueRunning) {
      this.processSerialQueue()
    }
  }

  private processSerialQueue(): void {
    const next = this.serialQueue.shift()
    if (!next) {
      this.serialQueueRunning = false
      return
    }

    this.serialQueueRunning = true
    // Open modal for the next product move
    setTimeout(() => {
      this.currentSerialMove = next
      this.modalSerialNumbers?.showChildModal(next)
      // Subscribe to completion events once; advance queue after close/confirm
      this.bindSerialModalAdvance()
    }, 0)
  }

  private bindSerialModalAdvance(): void {
    // Clean up any old subs to avoid duplicate advances
    this.serialQueueSubs.forEach(s => s?.unsubscribe())
    this.serialQueueSubs = []

    let advanced = false
    const advance = () => {
      if (advanced) return
      advanced = true
      // cleanup current listeners
      this.serialQueueSubs.forEach(s => s?.unsubscribe())
      this.serialQueueSubs = []
      // proceed with next item in the queue
      setTimeout(() => this.processSerialQueue(), 0)
    }

    // Case 1: Creating pack flow emits serialNumbersEdited
    const s1 = this.modalSerialNumbers?.serialNumbersEdited?.subscribe((serials: string[]) => {
      if (this.currentSerialMove) {
        this.currentSerialMove.serialNumbers = serials || []
        // reflect the change in operations list
        this.updateOperation(this.currentSerialMove)
      }
      advance()
    })
    if (s1) this.serialQueueSubs.push(s1)

    // Case 2: Non-pack flow closes the modal; ModalContainer emits `hide`
    const s2 = this.modalSerialNumbers?.modal?.hide?.subscribe(() => advance())
    if (s2) this.serialQueueSubs.push(s2)
  }

  persistCurrentSerialNumbersDraft(serialNumbers: string[]): void {
    this.persistSerialNumbersDraft(this.currentSerialMove, serialNumbers)
  }

  persistSerialNumbersDraft(operation: ProductMove | null, serialNumbers: string[]): void {
    if (!operation) return

    operation.serialNumbers = serialNumbers || []
    this.updateOperation(operation)
  }

  operationListChange(moves: ProductMove[]) {
    this.operations = moves
    this.saveViewSettings()
  }

  getOperationsOriginPosition(operation) {
    const a = this.movesOriginPositions.find(m => m.productMoveUuid === operation.uuid)
    return a?.originPosition
  }

  addOperationToList(operation: ProductMove): void {
    if (!operation || (operation && !operation.product)) {
      if (Array.isArray(operation) && operation.length > 0) {
        operation.forEach(err => {
          this.notificationService.error(
            this.translateService.instant('stock-operation-list.save.error', {
              error: err && err.message ? err.message : err,
            }),
            null,
            true
          )
        })
      } else {
        this.notificationService.error(this.translateService.instant('stock-operation-list.save.error.500'), null, true)
      }
      return
    }

    if (this.selectedOperationType !== 'out' && operation.amount < 1) {
      alert(this.translateService.instant('stock-operation-list.not-possible-to-store-in-minus'))
      return
    }

    operation = new ProductMove(operation)
    if (this.selectedOperationType === 'out') {
      this.handleOperationTypeOut(operation)
    } else {
      let exists = false
      this.operations.forEach((op: ProductMove, i: number) => {
        if (this.isProductInOperationsYet(op, operation)) {
          exists = true
          this.operations[i].amount = Number(op.amount) + Number(operation.amount)
          this.operations[i].purchasePriceSum =
            (Number(op.purchasePriceSum) || 0) + (Number(operation.purchasePriceSum) || 0)
          this.operations[i].supplier = operation.supplier
        }
      })
      if (!exists) {
        operation.purchasePriceSum = (Number(operation.purchasePrice) || 0) * (operation.amount || 1)
        this.operations.push(operation)
      }
      this.saveViewSettings()
    }
  }

  // updateOperation(operation: ProductMove): void {
  //   operation = new ProductMove(operation)
  //   if (this.selectedOperationType === 'out') {
  //     this.handleOperationTypeOut(operation)
  //   } else {
  //     this.operations.forEach((op: ProductMove, i: number) => {
  //       // if (op.product.id === operation.product.id && op.supplier === operation.supplier) { // todo: is supplier needed? best solution: uuids
  //       if (op.product.id === operation.product.id) {
  //         this.operations[i] = operation
  //         this.saveViewSettings()
  //         return
  //       }
  //     })
  //   }
  // }
  updateOperation(operation: ProductMove): void {
    let operationIndex = this.operations.indexOf(operation)
    if (operationIndex === -1 && operation?.uuid !== undefined && operation?.uuid !== null) {
      operationIndex = this.operations.findIndex(existing => String(existing.uuid) === String(operation.uuid))
    }
    if (operationIndex === -1) return

    const updatedOperation = operation instanceof ProductMove ? operation : new ProductMove(operation)
    this.operations = this.operations.map((existing, index) => (index === operationIndex ? updatedOperation : existing))
    this.saveViewSettings()
  }

  removeOperation(operation: ProductMove): void {
    this.operations.forEach((op: ProductMove, i: number) => {
      if (op === operation) {
        this.operations.splice(i, 1)
        this.saveViewSettings()
        return
      }
    })
  }

  async handleOperationTypeOut(selectedOperation: ProductMove): Promise<void> {
    this.operations.push(selectedOperation)
    this.saveViewSettings()
  }

  isProductInOperationsYet(operation, selectedOperation): boolean {
    return (
      operation.product.id === selectedOperation.product.id &&
      operation.purchasePrice === selectedOperation.purchasePrice &&
      operation.sellPrice === selectedOperation.sellPrice &&
      operation.batch === selectedOperation.batch &&
      operation.expirationDate === selectedOperation.expirationDate &&
      operation.purchaseCurrency === selectedOperation.purchaseCurrency &&
      operation.purchaseCurrencyDate === selectedOperation.purchaseCurrencyDate &&
      operation.position1 === selectedOperation.position1 &&
      operation.position2 === selectedOperation.position2 &&
      operation.position3 === selectedOperation.position3
    )
  }

  updateNote(note: string): void {
    this.note = note
    this.saveViewSettings()
  }

  updateExternalReference(externalReference: string): void {
    this.externalReference = externalReference
    this.saveViewSettings()
  }

  createStockLoad(): void {
    // this.loading = true
    if (this.selectedOperationType === 'out') {
      this.operations.forEach(op => {
        delete op.batch
        delete op.supplier
        delete op.expirationDate
        delete op.purchasePrice
      })
    }
    const movePack: ProductMovePack = new ProductMovePack({
      type: { id: this.selectedOperationType },
      depotFrom: {
        id: this.selectedDepotFrom(),
      },
      depotTo: {
        id: this.selectedDepotTo,
      },
      moves: this.operations,
    })
    if (this.note && this.note !== 'null') {
      movePack.note = this.note
    }
    if (this.externalReference && this.externalReference !== 'null') {
      movePack.externalReference = this.externalReference
    }
    // check if depots exists for sure in this company (prevents multiple company in multiple tabs chaos)
    let areDepotsOk = true
    movePack.moves.forEach(mo => {
      const doesSelectedDepotsOnMoveExists =
        this.queryDepotsOptions
          .data()
          .map(d => d.id)
          .filter(d => d === Number(mo.depotFrom) || d === Number(mo.depotTo)).length > 0
      if (!doesSelectedDepotsOnMoveExists && (mo.depotFrom || mo.depotTo)) {
        areDepotsOk = false
        return
      }
    })
    if (!areDepotsOk) {
      // todo !!!!!!!!!!!!!!!
      alert(this.translateService.instant('stock-operation-list.cannot-be-stocked'))
      return
    }
    this.core.services.productMovePack
      .create(movePack)
      .then(result => {
        if (result && result.id) {
          this.notificationService.success(
            this.translateService.instant(
              `ALERT ${
                this.selectedOperationType === 'in'
                  ? 'LOADING'
                  : this.selectedOperationType === 'out'
                    ? 'UNLOADING'
                    : 'TRANSFER'
              } STOCK`
            )
          )
          // this.loading = true
          window['selectedSupplier'] = undefined
          this.updateUploadedFilesWithProductMovePackId(result.id)
            .then(res => {
              // this.loading = false
              if (res.length) {
                this.notificationService.success(this.translateService.instant('stock-operation-list.success'))
              }
              this.clearList(true)
              this.queryProducts.refetch()
            })
            .catch(e => `createStockLoad err, on fileUpdate: ${e.message}`)
        } else {
          this.notificationService.warning(this.translateService.instant('stock-operation-list.processing-error'))
        }
        // this.loading = false
        this.loadViewSettings()
      })
      .catch(error => {
        // this.loading = false
        alert(
          this.translateService.instant('stock-operation-list.error') +
            (error && error.message ? error.message : typeof error === 'string' ? error : '')
        )
        console.warn(
          'StockOpList::createStockLoad ERROR',
          error,
          error && error.message ? error.message : typeof error === 'string' ? error : ''
        )
      })
  }

  printingSelect(type: string): void {
    let ops: any[] = []
    switch (type) {
      case 'all':
        ops = this.operations.map(op => {
          op.isEANPrintable = true
          return op
        })
        break
      case 'none':
        ops = this.operations.map(op => {
          op.isEANPrintable = false
          return op
        })
        break
      case 'invert':
        ops = this.operations.map(op => {
          op.isEANPrintable = !op.isEANPrintable
          return op
        })
        break
    }
    this.operations = ops
  }

  refreshOperationsHack(): void {
    // we need just to initiate pipe reload
    this.operations = [...this.operations]
    // .. and refresh tickets for print
    this.printCodeComponent?.hydrate()
  }

  async setOperationType(t: OperationType | string) {
    // this.loading = true
    this.selectedDepotTo = null
    this.selectedDepotFrom.set(null)
    this.selectedOperationType = t
    this.core.services.storage.clear(this.storageKey())
    this.saveViewSettings()
    // Explicit refresh after operation type change to sync the list with new context
    this.queryProducts.refetch()
  }

  uploadFiles(inputEvent: Event): Promise<Array<File> | any> {
    this.areFilesUploading = true
    const target = inputEvent.target as HTMLInputElement
    return new Promise((resolve, reject) => {
      if (target.files?.length) {
        const promisesBase64s = Array.from(target.files).map(f => this.fileUtil.processUploadedFileToBase64(f))
        Promise.allSettled(promisesBase64s)
          .then(base64s => {
            const fulfilledBase64s = getFulfilled<File>(base64s)
            const promisesUploads = fulfilledBase64s.map(b => {
              return this.core.services.file.create(new File(b), null, null, null, null, null, SchemaUtil.file)
            })
            return Promise.allSettled(promisesUploads).then(res => {
              const fulfilled = getFulfilled<File>(res)
              if (fulfilled.length) {
                this.uploadedFiles.push(...fulfilled)
                this.saveViewSettings()
                this.notificationService.success(this.translateService.instant('alert.file.uploaded'))
              }
              if (fulfilled.length < res.length) {
                const rejected = getRejected<File>(res)
                this.notificationService.error(
                  this.translateService.instant('alert.file.removed') + '\n' + rejected.join('\n')
                )
              }
            })
          })
          .catch(e => console.warn(`StockOperationList: uploadFiles err: ${e.stack}`))
          .finally(() => {
            this.areFilesUploading = false
          })
      } else {
        this.areFilesUploading = false
        reject(this.translateService.instant('no-file'))
      }
    })
  }

  updateUploadedFilesWithProductMovePackId(pmpId: number): Promise<Array<any>> {
    return new Promise((resolve, reject) => {
      if (!this.uploadedFiles?.length) {
        return resolve([])
      }
      const promises = this.uploadedFiles.map(f => this.core.services.file.update(f, null, null, null, null, pmpId))
      Promise.allSettled(promises).then(res => {
        const fulfilled = getFulfilled<File>(res)
        if (fulfilled?.length) {
          resolve(fulfilled)
        } else {
          reject(this.translateService.instant('no-file'))
        }
      })
    })
  }

  async removeUploadedFile(file: File) {
    if (!confirm(this.translateService.instant('delete-file.confirmation', { name: file.originalFilename }))) {
      return
    }
    if (file?.id) {
      try {
        await this.core.services.file.delete({ id: file.id }).then(() => {
          this.notificationService.success(this.translateService.instant('alert.file.removed'))
        })
        this.uploadedFiles.forEach((f, i) => {
          if (file.id === f.id) {
            this.uploadedFiles.splice(i, 1)
          }
        })
      } catch (e) {
        this.translateService.instant('stock-operation-list.delete.error', {
          filename: file.originalFilename,
        })
      }
      this.saveViewSettings()
    }
  }

  getProductDepotsWithQuantity(prod) {
    return prod.productDepots.filter(
      pDepot => Number(pDepot.depot.id) === Number(this.selectedDepotFrom()) && pDepot?.quantityStock > 0
    )
  }

  printList(): void {
    //TODO: why???????
    const htmlRows = this.operations
      .map(
        op => `
      <tr>
        <td>${op.product ? op.product.fullName : ''}</td>
        <td class="text-end">${op.product ? op.product.ean : ''}</td>
        <td class="text-end">${op.product ? op.product.code : ''}</td>
        <td class="text-end">${op.amount}</td>
        <td class="text-end">${op.purchasePrice !== 0 ? Number(op.purchasePrice).toFixed(2) : '0.00'}</td>
      </tr>`
      )
      .join('')
    const html = `
      <table>
        <thead>
          <tr>
            <th>Název</th>
            <th>EAN</th>
            <th>('product.code' | translate)</th>
            <th>Počet</th>
            <th>Nákupní cena</th>
          </tr>
        </thead>
        <tbody>${htmlRows}</tbody>
      </table>`
    const pWidth = 900
    const pHeight = 720
    // TODO: https://developer.mozilla.org/en-US/docs/Web/API/Window/open#target
    const popupWin = window.open(
      '',
      'Seznam položek k naskladnění',
      `toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=${pWidth},height=${pHeight},top=${
        screen.height - (screen.height + pHeight) / 2
      },left=${screen.width - (screen.width + pWidth) / 2}`
    )
    popupWin.document.body.innerHTML = html
    const styles = document.createElement('style')
    styles.innerHTML = `
      table {width: 100%; border: none; border-collapse: collapse;}
      td, th {border: 1px solid #333; padding: 1mm;}
      .text-end {text-align: right;}`
    popupWin.document.head.appendChild(styles)
    setTimeout(() => {
      popupWin.print()
    }, 300)

    setTimeout(() => {
      popupWin.close()
    }, 500)
  }

  getThumbUrl(image: File): string {
    return image.thumbnails?.length > 0 ? image.thumbnails.filter(t => t.format === 'w135wp')[0].url : ''
  }

  async addMovesFromPMP(pmpCode: string) {
    this.selectedOperationType = 'in'
    if (!this.selectedDepotTo) {
      alert(this.translateService.instant('modal-stock-operation-card.select-warehouse'))
      return
    }
    const pmp = await this.core.services.productMovePack.getById(
      Number(pmpCode.replace('pmp-', '')),
      SchemaUtil.productMovePack
    )
    this.externalReference = `${pmp.number}`
    for (const m of pmp.moves) {
      m.supplier = m.productDepotFrom?.supplier?.id
      m.depotTo = this.selectedDepotTo
      this.addOperationToList(m as any)
    }
  }
}
